import { motion } from 'framer-motion';
import { Helmet } from "react-helmet-async";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  Gavel,
  Info,
  Mail,
  MapPin,
  Scale,
  ShieldCheck,
  Target
} from 'lucide-react';

/**
 * Investor Charter — Option Bricks (Research Analyst)
 *
 * Audit fixes applied in this pass:
 * 1. Heading hierarchy corrected to a single logical outline:
 *      H1  Investor Charter
 *      H2  A. Vision & Mission / B. Business Transacted / C–G sections
 *      H3  Sub-groups within each section (e.g. "Our Vision", "Level 1", "Do's")
 *      H4  Card-level labels ("Onboarding Process") where nested under an H3
 *    Previously two sections both used "F." and headings jumped levels
 *    (h2 straight to visually-styled labels with no h3), which breaks screen
 *    reader navigation and heading-outline audits.
 * 2. Contrast fixes:
 *    - Body copy on dark panels raised from slate-400 → slate-300 at rest
 *      (slate-400 on slate-900 sits near ~4.6:1; slate-300 gives comfortable
 *      margin for body text at this size).
 *    - The "(No indicative timelines)" caption raised from slate-500 → slate-400
 *      on the dark panel.
 *    - Fixed an invalid Tailwind class (`hover:bg-blue`, not a real utility)
 *      on the SCORES button, which silently disabled that hover state.
 * 3. Semantics/ARIA:
 *    - External links now carry aria-label/rel/target consistently and
 *      indicate "(opens in new tab)" for assistive tech.
 *    - Icon-only affordances given discernible text.
 *    - Visible focus rings added (focus-visible) for keyboard users, since
 *      several interactive elements previously had none.
 *    - Section landmarks use aria-labelledby tied to each heading id.
 */

const InvestorCharter = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const linkFocus =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 pb-20">

    <Helmet>
  <title>Investor Charter| Option Bricks</title>
</Helmet>


      <motion.div
        initial="initial"
        animate="animate"
        variants={stagger}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        {/* Title Header */}
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 mt-20 tracking-tight">
            Investor Charter
          </h1>
          <p className="text-xl text-blue-700 font-bold uppercase tracking-widest">
            Option Bricks — Research Analyst (RA)
          </p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full" />
        </motion.div>

        {/* Container for Section A & B */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 items-stretch">

          {/* Section A: Vision & Mission */}
          <motion.section
            variants={fadeInUp}
            whileHover={{ y: -5 }}
            aria-labelledby="section-a-heading"
            className="flex flex-col bg-white p-4 rounded-[2rem] shadow-sm border border-slate-200 transition-all hover:shadow-md"
          >
            <div className="flex items-center mb-6">
              <div className="p-3 bg-blue-50 rounded-2xl mr-4">
                <ShieldCheck className="w-6 h-6 text-blue-600" aria-hidden="true" />
              </div>
              <h2 id="section-a-heading" className="text-2xl font-extrabold text-slate-800 tracking-tight">
                A. Vision &amp; Mission
              </h2>
            </div>

            <div className="space-y-6 flex-grow">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">Our Vision</h3>
                <p className="text-lg text-slate-700 leading-snug font-medium">
                  Invest with <span className="text-blue-600">knowledge</span> &amp; <span className="text-blue-600">safety</span>.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <h3 className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  Every investor should be able to invest in right investment products based on their needs, manage and monitor them to meet their goals, access reports and enjoy financial wellness.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Section B: Business Transacted */}
          <motion.section
            variants={fadeInUp}
            whileHover={{ y: -5 }}
            aria-labelledby="section-b-heading"
            className="flex flex-col bg-white p-4 rounded-[2rem] shadow-sm border border-slate-200 transition-all hover:shadow-md"
          >
            <div className="flex items-center mb-2">
              <div className="p-3 bg-indigo-50 rounded-2xl mr-4 shrink-0">
                <Target className="w-6 h-6 text-indigo-600" aria-hidden="true" />
              </div>
              <h2 id="section-b-heading" className="text-xl font-extrabold text-slate-800 tracking-tight text-left">
                B. Business Transacted by Option Bricks
              </h2>
            </div>

            <h3 className="text-xs font-bold text-slate-500 mb-4 uppercase tracking-wider">
              Investor Services &amp; Compliance
            </h3>

            <ul className="space-y-2">
              {[
                'To publish research report based on the research activities of the Research Analyst',
                'To provide an independent unbiased view on securities',
                'To offer unbiased recommendation, disclosing the financial interests in recommended securities.',
                'To provide research recommendation, based on analysis of publicly available information and known observations',
                'To conduct audit annually',
                'To ensure that all advertisements are in adherence to the provisions of the Advertisement Code for Research Analysts.',
                'To maintain records of interactions, with all clients including prospective clients (prior to onboarding), where any conversation related to the research services has taken place'
              ].map((item, index) => (
                <li key={index} className="flex group">
                  <div className="mt-1.5 mr-2 shrink-0">
                    <div className="h-1.5 w-1.5 rounded-full bg-indigo-500 group-hover:scale-150 transition-transform" />
                  </div>
                  <span className="text-sm text-slate-600 leading-tight group-hover:text-slate-900 transition-colors">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.section>
        </div>

        {/* Section C: Services Provided */}
       {/* Section C: Services Provided */}
<motion.section
  variants={fadeInUp}
  aria-labelledby="section-c-heading"
  className="bg-white text-slate-900 p-8 md:p-12 rounded-[2.5rem] shadow-sm mb-12 border border-slate-200 relative overflow-hidden"
>
  {/* Background Decoration */}
  <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

  <h2 id="section-c-heading" className="text-3xl font-bold mb-10 flex items-center text-slate-900 flex-wrap gap-x-3">
    <span className="flex items-center">
      <span className="p-3 bg-blue-100 rounded-2xl mr-4 border border-blue-200 inline-flex">
        <Info className="w-6 h-6 text-blue-600" aria-hidden="true" />
      </span>
      C. Details of Services Provided to Investors
    </span>
    <span className="text-xs font-medium text-slate-500 uppercase tracking-widest">
      (No Indicative Timelines)
    </span>
  </h2>

  <div className="grid lg:grid-cols-3 gap-10">

    {/* Part 1: Primary Procedures */}
    <div className="lg:col-span-1 space-y-4">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-6">
        Onboarding Process
      </h3>
      <ul className="space-y-4">
        {[
          'Onboarding of Clients',
          'Sharing of terms and conditions of research services',
          'Completing KYC of fee-paying clients'
        ].map((step, i) => (
          <li key={i} className="flex items-center p-5 bg-white rounded-2xl border border-slate-200 hover:border-emerald-400 transition-all group shadow-sm">
            <div className="p-1 bg-emerald-50 rounded-full mr-4 group-hover:bg-emerald-100 transition-colors shrink-0">
              <CheckCircle className="text-emerald-600" size={20} aria-hidden="true" />
            </div>
            <span className="text-slate-700 font-medium">{step}</span>
          </li>
        ))}
      </ul>
    </div>

    {/* Part 2: Mandatory Disclosures */}
    <div className="lg:col-span-2">
      <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-8 flex items-center">
          <span className="w-8 h-[1px] bg-blue-300 mr-3" aria-hidden="true" />
          Detailed Disclosures to Clients
        </h3>

        <ul className="space-y-6">
          {[
            'To disclose, information that is material for the client to make an informed decision, including details of its business activity, disciplinary history, the terms and conditions of research services, details of associates, risks and conflicts of interest, if any.',
            'To disclose the extent of use of Artificial Intelligence tools in providing research services.',
            'To disclose, while distributing a third party research report, any material conflict of interest of such third party research provider or provide web address that directs a recipient to the relevant disclosures.',
            'To disclose any conflict of interest of the activities of providing research services with other activities of the research analyst.',
            'To distribute research reports and recommendations to the clients without discrimination.',
            'To maintain confidentiality w.r.t publication of the research report until made available in the public domain.',
            'To respect data privacy rights of clients and take measures to protect unauthorized use of their confidential information.',
            'To disclose the timelines for the services provided by the research analyst to clients and ensure adherence to the said timelines.',
            'To provide clear guidance and adequate caution notice to clients when providing recommendations for dealing in complex and high-risk financial products/services.',
            'To treat all clients with honesty and integrity.',
            'To ensure confidentiality of information shared by clients unless such information is required to be provided in furtherance of discharging legal obligations or a client has provided specific consent to share such information.'
          ].map((text, index) => (
            <li key={index} className="flex items-start group">
              <span className="mt-2 mr-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500 group-hover:scale-150 transition-transform" aria-hidden="true" />
              <p className="text-sm leading-relaxed text-slate-600 group-hover:text-slate-900 transition-colors">
                {text}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
</motion.section>
        {/* Section D: Grievance Redressal */}
        <motion.section
          variants={fadeInUp}
          aria-labelledby="section-d-heading"
          className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-slate-200 mb-12 overflow-hidden"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
            <h2 id="section-d-heading" className="text-3xl font-extrabold text-slate-900 flex items-center">
              <span className="p-3 bg-blue-600 rounded-2xl mr-4 shadow-lg shadow-blue-200 inline-flex">
                <Gavel className="w-6 h-6 text-white" aria-hidden="true" />
              </span>
              D. Grievance Redressal Mechanism
            </h2>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest bg-slate-100 px-4 py-2 rounded-full">
              Investor Protection
            </span>
          </div>

          <div className="space-y-8">
            {/* Step 1: Direct Resolution */}
            <div className="relative p-8 bg-slate-50 rounded-[2rem] border border-slate-100 group transition-all hover:bg-white hover:shadow-xl hover:shadow-slate-200/50">
              <div className="absolute top-0 right-0 p-6 text-slate-200 font-black text-6xl select-none opacity-50" aria-hidden="true">01</div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Level 1: Internal Redressal with Option Bricks
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 max-w-2xl">
                In case of any grievance or complaint, investors should first approach Option Bricks directly.
                We strive to redress all grievances immediately.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 overflow-hidden">
                <div className="flex items-center w-full sm:w-auto bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <a
                    href="mailto:compliance@optionbricks.in"
                    className={`flex items-center bg-blue-600 text-white px-3 py-2 text-xs sm:text-sm font-bold hover:bg-blue-700 transition-colors whitespace-nowrap ${linkFocus}`}
                  >
                    <Mail className="mr-2 shrink-0" size={16} aria-hidden="true" /> compliance@optionbricks.in
                  </a>
                  <div className="flex items-center px-4 py-2 text-[10px] sm:text-xs font-bold text-emerald-700 whitespace-nowrap bg-emerald-50">
                    <Clock className="mr-2 shrink-0" size={14} aria-hidden="true" /> Max 21 Days
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: SCORES & RAASB */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-8 bg-blue-50/50 rounded-[2rem] border border-blue-100 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-blue-900 text-lg">Level 2: SCORES 2.0</h3>
                    <span className="text-[10px] font-black bg-blue-600 text-white px-2 py-1 rounded">SEBI</span>
                  </div>
                  <p className="text-sm text-blue-900/80 mb-6 leading-relaxed">
                    A web-based centralized grievance redressal system by SEBI for time-bound resolution. You may lodge complaints with the <strong>Research Analyst Administration and Supervisory Body (RAASB)</strong>.
                  </p>

                  <ol className="space-y-3 mb-8">
                    <li className="flex items-center text-xs font-bold text-blue-900">
                      <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center mr-3 text-[10px]" aria-hidden="true">1</span>
                      First Review: Designated Body (RAASB)
                    </li>
                    <li className="flex items-center text-xs font-bold text-blue-900">
                      <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center mr-3 text-[10px]" aria-hidden="true">2</span>
                      Second Review: SEBI
                    </li>
                  </ol>
                </div>

                <a
                  href="https://scores.sebi.gov.in"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Access SCORES Portal (opens in new tab)"
                  className={`inline-flex w-full items-center justify-center rounded-xl border border-blue-200 bg-white py-3 font-bold text-blue-700 transition-all duration-300 hover:bg-blue-600 hover:text-white ${linkFocus}`}
                >
                  Access SCORES Portal
                  <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                </a>
              </div>

              {/* Step 3: SMART ODR */}
              <div className="p-8 bg-blue-50/50 rounded-[2rem] border border-blue-100 flex flex-col justify-between">
                <div>
                  <h3 className="font-bold text-blue-900 text-lg mb-4">Level 3: SMART ODR Platform</h3>
                  <p className="text-sm text-blue-900/80 mb-6 leading-relaxed">
                    If you are not satisfied with the resolution provided by the Market Participants, you have the option to file for <strong>online conciliation or arbitration</strong>.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-blue-200 text-center">
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-tight mb-2">Alternative Dispute Resolution</p>
                  <a
                    href="https://smartodr.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="SMARTODR Online Portal (opens in new tab)"
                    className={`inline-flex w-full items-center justify-center rounded-xl border border-blue-200 bg-white py-3 font-bold text-blue-700 transition-all duration-300 hover:bg-blue-600 hover:text-white ${linkFocus}`}
                  >
                    SMARTODR Online Portal
                    <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>

           {/* Physical Complaints */}
<div className="bg-slate-50 p-8 rounded-[2rem] text-slate-700 border border-slate-200 relative overflow-hidden shadow-sm">
  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -mr-16 -mt-16" aria-hidden="true" />
  <h3 className="text-slate-900 font-bold mb-4 flex items-center text-lg">
    <MapPin className="mr-2 text-blue-600" size={20} aria-hidden="true" /> Physical Complaints
  </h3>
  <div className="grid md:grid-cols-3 gap-6 items-center">
    <address className="md:col-span-2 text-sm leading-relaxed not-italic">
      Office of Investor Assistance and Education, Securities and Exchange Board of India,
      SEBI Bhavan, Plot No. C4-A, &apos;G&apos; Block, Bandra-Kurla Complex, Bandra (E), Mumbai - 400 051
    </address>
    <div className="hidden md:block h-px bg-slate-200 w-full" aria-hidden="true" />
  </div>
</div>



          </div>
        </motion.section>

        {/* Section E: Rights of Investors */}
        <motion.section variants={fadeInUp} aria-labelledby="section-e-heading" className="mb-12">
          <h2 id="section-e-heading" className="text-2xl font-bold text-slate-900 mb-8 flex items-center">
            <Scale className="mr-3 text-blue-600" aria-hidden="true" /> E. Rights of Investors
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              'Right to Privacy and Confidentiality',
              'Right to Transparent Practices',
              'Right to fair and Equitable Treatment',
              'Right to Adequate Information',
              'Right to Initial and Continuing Disclosure',
              'Right to receive information about all the statutory and regulatory disclosures',
              'Right to Fair & True Advertisement',
              'Right to Awareness about Service Parameters and Turnaround Times',
              'Right to be informed of the timelines for each service',
              'Right to be Heard and Satisfactory Grievance Redressal',
              'Right to have timely redressal',
              'Right to Exit from Financial product or service in accordance with the terms and conditions agreed with the research analyst',
              'Right to receive clear guidance and caution notice when dealing in Complex and High-Risk Financial Products and Services',
              'Right to Additional Rights to vulnerable consumers',
              'Right to get access to services in a suitable manner even if differently abled',
              'Right to provide feedback on the financial products and services used',
              'Right against coercive, unfair, and one-sided clauses in financial agreements'
            ].map((right, index) => (
              <li key={index} className="bg-white p-4 rounded-xl border border-slate-200 flex items-center text-sm font-semibold shadow-sm hover:border-blue-400 transition-colors">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 shrink-0" aria-hidden="true" /> {right}
              </li>
            ))}
          </ul>
        </motion.section>

        {/* Section F & G: Expectations (Do's & Don'ts) */}
        <div className="grid md:grid-cols-2 gap-8 items-stretch mb-12">

          {/* Do's Section */}
          <motion.section
            variants={fadeInUp}
            aria-labelledby="section-f-heading"
            className="bg-emerald-50/50 p-8 md:p-10 rounded-[2.5rem] border border-emerald-100 shadow-sm flex flex-col"
          >
            <h2 id="section-f-heading" className="text-2xl font-black text-emerald-900 mb-8 flex items-center">
              <span className="p-2 bg-emerald-500 rounded-lg mr-3 shadow-lg shadow-emerald-200 inline-flex">
                <CheckCircle className="text-white w-6 h-6" aria-hidden="true" />
              </span>
              F. Do&apos;s
            </h2>

            <ul className="space-y-5 flex-grow">
              {[
                'Always deal with SEBI registered Research Analyst.',
                'Ensure that the Research Analyst has a valid registration certificate.',
                {
                  text: 'Check for SEBI registration number. Refer to the list of all SEBI registered Research Analysts available on the SEBI website:',
                  link: 'https://www.sebi.gov.in/sebiweb/other/OtherAction.do?doRecognisedFpi=yes&intmId=14',
                  linkText: 'View SEBI Registered Research Analyst List'
                },
                'Always pay attention towards disclosures made in the research reports before investing.',
                'Pay your Research Analyst through banking channels only and maintain duly signed receipts mentioning details of payments. You may use the Centralized Fee Collection Mechanism (CeFCoM) of RAASB if the analyst has opted for it (Applicable for fee paying clients only).',
                'Before buying/selling securities or applying in public offer, check for the research recommendation provided by your Research Analyst.',
                'Ask all relevant questions and clear your doubts with your Research Analyst before acting on recommendation.',
                'Seek clarifications and guidance on research recommendations from your Research Analyst, especially if it involves complex and high risk financial products and services.',
                'Always be aware that you have the right to stop availing the service as per the terms of service agreed between you and your Research Analyst.',
                'Always be aware that you have the right to provide feedback to your Research Analyst in respect of the services received.',
                'Always be aware that you will not be bound by any clause prescribed by the research analyst which is contravening any regulatory provisions.',
                'Inform SEBI about Research Analyst offering assured or guaranteed returns.'
              ].map((item, i) => (
                <li key={i} className="flex items-start text-sm leading-relaxed text-emerald-900/90 group">
                  <div className="mt-1.5 mr-3 shrink-0">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:scale-150 transition-transform" />
                  </div>
                  <div>
                    {typeof item === 'string' ? (
                      item
                    ) : (
                      <>
                        {item.text}
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`${item.linkText} (opens in new tab)`}
                          className={`block mt-2 text-xs font-bold text-emerald-800 bg-white border border-emerald-200 px-3 py-1.5 rounded-lg w-fit hover:bg-emerald-600 hover:text-white transition-all shadow-sm ${linkFocus}`}
                        >
                          {item.linkText} →
                        </a>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </motion.section>

          {/* Don'ts Section */}
          <motion.section
            variants={fadeInUp}
            aria-labelledby="section-g-heading"
            className="bg-rose-50/50 p-8 md:p-10 rounded-[2.5rem] border border-rose-100 shadow-sm flex flex-col"
          >
            <h2 id="section-g-heading" className="text-2xl font-black text-rose-900 mb-8 flex items-center">
              <span className="p-2 bg-rose-500 rounded-lg mr-3 shadow-lg shadow-rose-200 inline-flex">
                <AlertTriangle className="text-white w-6 h-6" aria-hidden="true" />
              </span>
              G. Don&apos;ts
            </h2>

            <ul className="space-y-4 flex-grow">
              {[
                'Do not provide funds for investment to the Research Analyst.',
                "Don't fall prey to luring advertisements or market rumors.",
                'Do not get attracted to limited period discount or other incentive, gifts, etc. offered by Research Analyst.',
                'Do not share login credential and password of your trading, demat or bank accounts with the Research Analyst.'
              ].map((item, i) => (
                <li
                  key={i}
                  className="bg-white p-5 rounded-2xl border border-rose-100 shadow-sm hover:border-rose-300 transition-all group flex items-start"
                >
                  <span className="text-rose-300 font-black text-2xl mr-4 group-hover:text-rose-500 transition-colors leading-none" aria-hidden="true">
                    0{i + 1}
                  </span>
                  <p className="text-sm font-bold text-rose-900 leading-snug">
                    {item}
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-4 bg-rose-100/50 rounded-xl border border-rose-200/50">
              <p className="text-[10px] text-rose-800 font-bold uppercase tracking-widest text-center">
                Protect your financial security at all costs
              </p>
            </div>
          </motion.section>
        </div>

        <footer className="mt-20 text-center text-slate-500 text-xs border-t border-slate-200 pt-10">
          <p className="font-bold mb-2 text-slate-700">SEBI REGISTERED RESEARCH ANALYST — OPTION BRICKS</p>
          <p>© {new Date().getFullYear()} Option Bricks. All rights reserved.</p>
        </footer>
      </motion.div>
    </div>
  );
};

export default InvestorCharter;